import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, User, ChevronRight, LogOut, Lock } from "lucide-react";
import Logo from "@/components/layout/Logo";
import CareTeamTabs from "@/components/layout/CareTeamTabs";

const PAGE_SIZE = 50;

/**
 * Care Team → Clients tab.
 *
 * Searches the **entire brand** customer registry (not just clients the
 * caller has personally treated). The API is paginated so a brand with
 * 20k+ guests still loads snappily — we fetch 50 rows at a time and
 * pull the next page as the last row scrolls into view (IntersectionObserver).
 * Phone / email stay hidden here and in the restricted profile route.
 */
export default function MyClients() {
  const nav = useNavigate();
  const { staff, logoutStaff } = useAuth();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  // Bumped whenever the caller edits the search so any in-flight
  // pagination request from a stale query gets discarded on arrival.
  const queryTokenRef = useRef(0);

  const fetchPage = useCallback(async (opts) => {
    const { search, offset: off, replace } = opts;
    const token = queryTokenRef.current;
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const params = { offset: off, limit: PAGE_SIZE };
      if (search && search.trim()) params.search = search.trim();
      const r = await staffApi.get("/staff/my-clients", { params });
      // Ignore stale responses (user typed something newer already).
      if (token !== queryTokenRef.current) return;
      const nextRows = r.data?.rows || [];
      setTotal(r.data?.total || 0);
      setHasMore(!!r.data?.has_more);
      setOffset(off + nextRows.length);
      setRows((cur) => (replace ? nextRows : [...cur, ...nextRows]));
    } catch (err) {
      if (token === queryTokenRef.current) toast.error(formatApiError(err));
    } finally {
      if (token === queryTokenRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  // Debounced search — every keystroke bumps the token so stale
  // pagination results get dropped when they land after the user has
  // moved on.
  useEffect(() => {
    queryTokenRef.current += 1;
    setRows([]);
    setOffset(0);
    setHasMore(false);
    setTotal(0);
    const t = setTimeout(() => {
      fetchPage({ search: query, offset: 0, replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [query, fetchPage]);

  // Infinite scroll — observe the sentinel row and pull the next page
  // when it enters the viewport.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting && hasMore && !loading && !loadingMore) {
        fetchPage({ search: query, offset, replace: false });
      }
    }, { rootMargin: "200px" });
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, offset, query, fetchPage]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const letter = (r.full_name || "?").trim().charAt(0).toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : "#";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const openClient = (cid) => {
    if (!cid) return;
    nav(`/staff/my-roster/client?cid=${cid}`);
  };

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)] pb-20 sm:pb-6">
      <header className="sticky top-0 z-10 h-14 px-4 sm:px-6 border-b border-border bg-white/95 backdrop-blur flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="mark" to={null} size={32} testid="myclients-brand-logo" />
          <div className="hidden sm:block">
            <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
            <p className="text-sm font-semibold leading-tight">Client Registry</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground max-w-[8rem] truncate">{staff?.full_name}</span>
          <Button variant="ghost" size="sm" onClick={() => { logoutStaff(); nav("/staff/login"); }} data-testid="myclients-logout-btn">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="hidden sm:block max-w-2xl mx-auto pt-4 px-3 sm:px-6">
        <CareTeamTabs />
      </div>

      <div className="max-w-2xl mx-auto px-3 sm:px-6 py-6">
        <div className="mb-4 sm:hidden">
          <p className="text-[11px] tracking-overline text-muted-foreground">CARE TEAM DISPATCH</p>
          <h1 className="text-2xl font-semibold mt-1">Client Registry</h1>
          <p className="text-xs text-muted-foreground mt-1">Search every client across the brand. Tap to open the restricted medical profile.</p>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all clients by full name…"
            className="pl-9 h-11 rounded-full bg-white"
            data-testid="myclients-search-input"
            autoFocus
          />
        </div>

        <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
          <span data-testid="myclients-count">
            {loading
              ? "Searching…"
              : (query.trim()
                ? `${total} match${total === 1 ? "" : "es"}`
                : `${total.toLocaleString()} clients in brand`)}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider">
            <Lock className="w-3 h-3" /> Phone / email hidden
          </span>
        </div>

        {loading && rows.length === 0 && (
          <div className="space-y-2" data-testid="myclients-loading">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse h-14 rounded-xl bg-white border border-border" />
            ))}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-14 text-sm text-muted-foreground" data-testid="myclients-empty">
            {query.trim() ? "No clients match that search." : "No clients yet."}
          </div>
        )}

        {rows.length > 0 && (
          <div className="space-y-4" data-testid="myclients-list">
            {grouped.map(([letter, list]) => (
              <div key={letter}>
                <div className="text-[11px] tracking-overline text-muted-foreground px-1 mb-1">{letter}</div>
                <ul className="space-y-2">
                  {list.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => openClient(c.id)}
                        className="w-full text-left rounded-xl bg-white border border-border hover:border-primary hover:shadow-sm transition-all p-3 flex items-center gap-3"
                        data-testid={`myclients-row-${c.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.full_name}</div>
                          {c.last_seen_iso ? (
                            <div className="text-[11px] text-muted-foreground truncate">
                              Last seen {new Date(c.last_seen_iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                            </div>
                          ) : (
                            <div className="text-[11px] text-muted-foreground italic">New to you</div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Sentinel + loading state for infinite scroll. */}
            <div ref={sentinelRef} data-testid="myclients-sentinel" className="h-8" />
            {loadingMore && (
              <div className="text-center text-xs text-muted-foreground py-2" data-testid="myclients-loading-more">
                Loading more…
              </div>
            )}
            {!hasMore && !loadingMore && rows.length > 0 && (
              <div className="text-center text-[11px] text-muted-foreground py-3" data-testid="myclients-end">
                End of list · {rows.length.toLocaleString()} shown
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sm:hidden">
        <CareTeamTabs />
      </div>
    </div>
  );
}
